import { useMemo, useState } from 'react'
import { Outlet } from 'react-router'

import { AppHeader } from './AppHeader.js'
import { BarTitleProvider } from './BarTitleProvider.js'
import { LegendSlotContext } from '../components/legend-slot.js'
import { ScaleContext } from './scale-context.js'
import { SoundProvider } from '../sound/SoundProvider.js'
import { useFrameScroll } from './useFrameScroll.js'

/**
 * The host display, shown on a television.
 *
 * **Framed like the games it is showing**, which it was not. This screen was
 * built on the premise that "nobody walks up to a television to click things",
 * so it had no bar above and no legend slot below — the legend fell back to
 * drawing itself inline, wherever the game happened to end.
 *
 * That premise has been overtaken. The room is driven from a Bluetooth
 * keyboard: it has a card menu, a number for every card, Escape, and a key
 * that closes the room. It needed the same frame as the menu three feet away,
 * and having one is also what bounds the height a board is allowed — without
 * it every board sized itself to its own contents, which is why Hangman's race
 * was as wide as the word (DOL-94).
 *
 * No account controls in the bar, because the television has no session and
 * never will: it is a screen, not a player.
 */
export function TvLayout() {
  /*
   * Fixed, and not written down.
   *
   * The attribute below is what the CSS reads, but anything inside that asks
   * the hook needs the same answer — and a television by definition should not
   * be teaching this browser to show every other page at television size.
   */
  const fixed = useMemo(() => ({ scale: 'tv' as const, choose: () => undefined }), [])

  /*
   * The legend's landing place, and the scroll region — both held as state
   * rather than refs, and both for the same reason as in `AppLayout`: a portal
   * and a scroll hook each need the node itself, and a ref is null on the
   * first render with nothing to re-render and notice it has filled in.
   */
  const [legendSlot, setLegendSlot] = useState<HTMLElement | null>(null)
  const [frame, setFrame] = useState<HTMLElement | null>(null)

  useFrameScroll(frame, true)

  return (
    <ScaleContext.Provider value={fixed}>
      {/* Above the bar, so the bar can read what the room is showing — and the
          same rule as the ordinary layout, because it is the same bar. */}
      <BarTitleProvider>
        {/*
        Exactly a viewport, and the shell itself cannot scroll — the same shape
        as the ordinary layout at this scale. What scrolls is the region in the
        middle, so the bar stays at the top and the legend stays at the bottom
        however long the thing between them is.
      */}
        <div data-scale="tv" className="bg-surface text-ink flex h-dvh flex-col overflow-hidden">
          <AppHeader />

          <main
            ref={setFrame}
            className="flex min-h-0 w-full flex-1 flex-col overflow-y-auto p-8 [scrollbar-gutter:stable]"
          >
            {/*
            Exactly the region, and nothing is centred here.

            Two earlier attempts, both instructive. `justify-center` on a box
            of `min-h-full` clipped: the box is a flex item, `flex-shrink: 1`
            let it become shorter than its contents, and centring pushed half a
            board off the top where the scroll region could not reach it —
            105px of overflow at `scrollTop` 0 (DOL-100). Adding `shrink-0`
            fixed that, but left the height indefinite, and a board cannot
            size itself to a screen that has no size: Sky's twenty cards still
            ran 205px past a 1080 screen with three people seated.

            `h-full` is a definite height, which is what `flex-1` inside a
            board needs in order to divide it. Nothing is centred, so nothing
            can be centred out of view: content shorter than the region sits
            under the bar, and content taller overflows a box that does not
            clip, so the region scrolls to it from the top.
          */}
            <div className="flex h-full w-full flex-col items-center">
              <LegendSlotContext.Provider value={legendSlot}>
                {/*
                The host display is a television by definition — it is the
                thing with the speakers pointed at the room — so it always
                makes a noise, where AppLayout has to ask what scale it is
                being read at.
              */}
                <SoundProvider heard>
                  <Outlet />
                </SoundProvider>
              </LegendSlotContext.Provider>
            </div>
          </main>

          {/*
          The key legend lands here, below everything else and outside the
          column the board is laid out in — full width, at the bottom of the
          screen, taking exactly the height its contents need rather than a
          height somebody guessed.
        */}
          <div ref={setLegendSlot} />
        </div>
      </BarTitleProvider>
    </ScaleContext.Provider>
  )
}
